import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;
const api = axios.create({
    baseURL: API_URL,
    timeout: 10000,
});

// Health Check Function
export const warmUpServer = async () => {
    try {
        const response = await api.get('/health');
        console.log('Server is warmed up:', response.data);
        return response.data;
    } catch (error) {
        console.error('Error warming up server:', error);
    }
};

// Existing functions...
export const fetchProducts = async ({
                                        page = 1,
                                        limit = 100,
                                        category = null,
                                        tags = [],
                                        search = '',
                                        sortBy = 'createdAt',
                                        sortOrder = 'desc'
                                    } = {}, retries = 3) => {
    try {
        const params = {
            page,
            limit,
            sortBy,
            sortOrder,
        };
        if (category) params.category = category;
        if (tags && tags.length > 0) params.tags = tags.join(',');
        if (search) params.search = search;

        const response = await api.get('/products', { params });
        return response.data;
    } catch (error) {
        if (retries > 0) {
            console.warn(`Retrying... ${retries} attempts left`);
            return fetchProducts({ page, limit, category, tags, search, sortBy, sortOrder }, retries - 1);
        }
        console.error('All retry attempts failed:', error);
        throw new Error(error.response?.data?.message || 'Failed to fetch products');
    }
};

export const fetchProductById = async (id) => {
    try {
        const response = await api.get(`/products/${id}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching product:', error);
        throw new Error(
            error.response?.data?.message ||
            'Failed to fetch product'
        );
    }
};

export const fetchCategories = async () => {
    try {
        const response = await api.get('/products/categories');
        return response.data;
    } catch (error) {
        console.error('Error fetching categories:', error);
        throw new Error(
            error.response?.data?.message ||
            'Failed to fetch categories'
        );
    }
};

export const fetchTags = async () => {
    try {
        const response = await api.get('/products/tags');
        return response.data;
    } catch (error) {
        console.error('Error fetching tags:', error);
        throw new Error(
            error.response?.data?.message ||
            'Failed to fetch tags'
        );
    }
};

export const login = async (username, password) => {
    try {
        const response = await api.post('/auth/login', { username, password });
        return response.data; // Response should include the token
    } catch (error) {
        console.error('Error during login:', error);
        throw new Error(
            error.response?.data?.message ||
            'Login failed'
        );
    }
};

export const logout = async (token) => {
    try {
        await api.post('/auth/logout', {}, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        localStorage.removeItem('token');
    } catch (error) {
        console.error('Error during logout:', error);
        // Still remove token even if API call fails
        localStorage.removeItem('token');
    }
};

export const addProduct = async (productData, token) => {
    try {
        if (productData.super_price >= productData.mrp) {
            throw new Error('Super price cannot be greater than MRP');
        }
        
        const response = await api.post('/products/add', productData, {
            headers: { 
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error adding product:', error);
        throw new Error(
            error.response?.data?.message ||
            error.message ||
            'Failed to add product'
        );
    }
};

export const deleteProduct = async (id, token) => {
    try {
        const response = await api.delete(`/products/${id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to delete product');
    }
};

export const bulkDeleteProducts = async (productIds, token) => {
    try {
        const response = await api.post('/products/bulk-delete', 
            { productIds },
            {
                headers: { 'Authorization': `Bearer ${token}` }
            }
        );
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to delete products');
    }
};

export const updateProduct = async (id, productData, token) => {
    try {
        const response = await api.put(`/products/${id}`, productData, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to update product');
    }
};

// Upload Excel file
export const uploadExcelFile = async (file, token) => {
    try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await api.post('/upload/excel', formData, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error uploading file:', error);
        throw new Error(
            error.response?.data?.message ||
            'Failed to upload file'
        );
    }
};

// Get job status
export const getJobStatus = async (jobId, token) => {
    try {
        const response = await api.get(`/upload/job/${jobId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to get job status');
    }
};

// Get all job statuses
export const getAllJobStatuses = async (token) => {
    try {
        const response = await api.get('/upload/jobs', {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to get job statuses');
    }
};

// Settings API - Get Instagram posts (public endpoint for homepage - returns embed data)
export const getInstagramPosts = async () => {
    try {
        // Use the instagram endpoint which fetches from settings and gets oEmbed data
        const response = await api.get('/instagram/posts');
        return response.data;
    } catch (error) {
        console.error('Error in getInstagramPosts:', error);
        throw new Error(error.response?.data?.message || error.message || 'Failed to get Instagram posts');
    }
};

// Settings API - Get Instagram post URLs (for admin settings page - returns raw URLs)
export const getInstagramPostUrls = async (token) => {
    try {
        const response = await api.get('/settings/instagram/posts', {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        return response.data;
    } catch (error) {
        console.error('Error in getInstagramPostUrls:', error);
        throw new Error(error.response?.data?.message || error.message || 'Failed to get Instagram post URLs');
    }
};

export const updateInstagramPosts = async (posts, token) => {
    try {
        const response = await api.put('/settings/instagram/posts', 
            { posts },
            {
                headers: { 'Authorization': `Bearer ${token}` }
            }
        );
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to update Instagram posts');
    }
};